import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';
import { ExternalLink, RefreshCw, Image, BarChart3, Plus, PinIcon } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  connected: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  error: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
};

export default function PinterestPage() {
  const [activeTab, setActiveTab] = useState('connect');
  const [boards, setBoards] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [oauthConnected, setOauthConnected] = useState(false);
  const [oauthEmail, setOauthEmail] = useState('');

  // Create pin form
  const [boardId, setBoardId] = useState('');
  const [pinTitle, setPinTitle] = useState('');
  const [pinDescription, setPinDescription] = useState('');
  const [pinLink, setPinLink] = useState('');
  const [pinMediaUrl, setPinMediaUrl] = useState('');

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  /** Pinterest API returns metrics under `analytics`; flatten for UI. */
  const normalizeAnalyticsPayload = (data) => {
    if (!data || typeof data !== 'object') return null;
    const inner = data.analytics;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return { ...inner };
    }
    const { success: _s, ...rest } = data;
    return Object.keys(rest).length ? rest : null;
  };

  const formatMetricValue = (val) => {
    if (val == null) return '—';
    if (typeof val === 'number' && Number.isFinite(val)) return val.toLocaleString();
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return '—';
      }
    }
    return String(val);
  };

  const loadBoards = async () => {
    try {
      const { data } = await api.get('/pinterest/boards');
      setBoards(data.boards || []);
    } catch {
      // silent — not connected yet
    }
  };

  const loadAnalytics = async () => {
    try {
      const { data } = await api.get('/pinterest/analytics');
      setAnalytics(normalizeAnalyticsPayload(data));
    } catch {
      setAnalytics(null);
    }
  };

  const loadOauthStatus = async () => {
    try {
      const { data } = await api.get('/integrations/oauth/pinterest/status');
      setOauthConnected(!!data.connected);
      setOauthEmail(data.connected_email || '');
    } catch {
      setOauthConnected(false);
      setOauthEmail('');
    }
  };

  useEffect(() => {
    loadBoards();
    loadAnalytics();
    loadOauthStatus();
  }, []);

  const handleConnect = async () => {
    setLoading(true);
    setStatus('');
    try {
      const { data } = await api.get('/integrations/oauth/pinterest/authorize');
      if (data.auth_url) {
        // Same flow as Integrations hub — real client_id + redirect via API_PUBLIC_URL
        window.location.href = data.auth_url;
        return; // browser navigates away
      }
      setStatus('Error: No authorization URL returned.');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePin = async (e) => {
    e.preventDefault();
    if (!boardId) {
      setStatus('Error: Select a board first.');
      return;
    }
    setLoading(true);
    setStatus('Creating pin...');
    try {
      await api.post('/pinterest/pins', {
        board_id: boardId,
        title: pinTitle,
        description: pinDescription,
        link: pinLink,
        media_url: pinMediaUrl,
      });
      setStatus('Pin created successfully!');
      setPinTitle('');
      setPinDescription('');
      setPinLink('');
      setPinMediaUrl('');
      setBoardId('');
      setActiveTab('boards');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'connect', label: 'Connect' },
    { id: 'boards', label: `Boards (${boards.length})` },
    { id: 'create-pin', label: 'Create Pin' },
    { id: 'analytics', label: 'Analytics' },
  ];

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
          <PinIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Pinterest</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Manage Pinterest boards, create pins, and track performance for visual SEO.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Boards', value: boards.length },
          { label: 'Impressions', value: analytics?.impressions ?? '—' },
          { label: 'Engagements', value: analytics?.engagements ?? '—' },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stat.value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`pb-2 px-1 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        {/* CONNECT TAB */}
        {activeTab === 'connect' && (
          <div className="space-y-5 max-w-md">
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">Connect Pinterest Account</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Authorize GDS to manage your Pinterest boards and pins for off-page SEO.
              </p>
            </div>
            {oauthConnected && (
              <div className={`rounded-md px-3 py-2 text-sm ${STATUS_STYLES.connected}`}>
                Connected{oauthEmail ? ` as ${oauthEmail}` : ''}. Boards and analytics use this account.
              </div>
            )}
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              {['Create and schedule pins to boards', 'Monitor impressions and engagement', 'Build backlinks through visual content'].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={handleConnect}
              disabled={loading}
              className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
            >
              <PinIcon className="w-4 h-4" />
              {loading ? 'Redirecting…' : oauthConnected ? 'Reconnect Pinterest' : 'Connect with Pinterest'}
            </button>
          </div>
        )}

        {/* BOARDS TAB */}
        {activeTab === 'boards' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">{boards.length} boards</p>
              <button
                type="button"
                onClick={() => { loadBoards(); loadAnalytics(); loadOauthStatus(); }}
                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {boards.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Image className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No boards found. Connect your account first.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {boards.map((board) => (
                  <div key={board.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{board.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{board.description || 'No description'}</p>
                        <p className="text-xs text-slate-400 mt-1">{board.pin_count ?? 0} pins</p>
                      </div>
                      {board.url && (
                        <a
                          href={board.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-red-500"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CREATE PIN TAB */}
        {activeTab === 'create-pin' && (
          <form onSubmit={handleCreatePin} className="space-y-5 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Board <span className="text-red-500">*</span>
              </label>
              {boards.length > 0 ? (
                <select
                  value={boardId}
                  onChange={(e) => setBoardId(e.target.value)}
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  required
                >
                  <option value="">Select a board</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-slate-400">Connect your account first to see boards.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Pin Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={pinTitle}
                onChange={(e) => setPinTitle(e.target.value)}
                placeholder="10 SEO Tips for 2026"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Description
              </label>
              <textarea
                value={pinDescription}
                onChange={(e) => setPinDescription(e.target.value)}
                placeholder="Discover actionable SEO strategies..."
                rows={3}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Destination Link <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={pinLink}
                  onChange={(e) => setPinLink(e.target.value)}
                  placeholder="https://yoursite.com/article"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Image URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={pinMediaUrl}
                  onChange={(e) => setPinMediaUrl(e.target.value)}
                  placeholder="https://yoursite.com/image.png"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || boards.length === 0}
              className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-md text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              {loading ? 'Creating...' : 'Create Pin'}
            </button>
          </form>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">Account Analytics</h2>
              <button
                type="button"
                onClick={loadAnalytics}
                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {!analytics ? (
              <div className="text-center py-12 text-slate-400">
                <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No analytics data. Connect your account first.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(analytics)
                  .filter(([k]) => k !== 'success' && k !== 'analytics')
                  .map(([key, val]) => (
                    <div key={key} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {formatMetricValue(val)}
                      </p>
                      <p className="text-xs text-slate-500 capitalize mt-1">{key.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            status.startsWith('Error')
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200'
          }`}>
            {status}
          </div>
        )}
      </div>
    </motion.div>
  );
}
